import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { canOpenPaywall } from '@/access/access';
import { useAccess } from '@/access/useAccess';
import type { PlanSlug } from '@/api/types';
import { setPaymentEventsEnabled, track } from '@/analytics/tracker';
import { useAuth } from '@/auth/AuthProvider';
import { appConfig } from '@/config/app';
import { MockCheckout } from './MockCheckout';
import { PaywallModal } from './PaywallModal';
import { usePaddleRetain } from './usePaddleRetain';
import { useSubscriptionPlans } from './useSubscriptionPlans';

/**
 * Три входа в окно подписки (BILL-2): явное намерение (Upgrade, Subscribe,
 * dock → `subscribe_click`), тапвайр (Show more → `paywall_shown`) и перехват
 * `?action=subscribe&plan=` с лендинга (`subscribe_click{source:'landing'}` + автозапуск Paddle).
 */
export type PaywallTrigger = 'intent' | 'tripwire' | 'landing_intent';
export type PaywallSource =
  | 'upgrade_button'
  | 'free_banner'
  | 'show_more'
  | 'paywall_dock'
  | 'billing_page'
  | 'landing'
  | 'canvas_limit'
  | 'canvas_share'
  | 'detail_quota'
  | 'quota_badge'
  /** Строка в панели разбора после последнего бесплатного просмотра дня (intent). */
  | 'quota_last_view';
/** Что именно упёрлось в бесплатный тариф — строка над тарифами в окне оплаты. */
export type PaywallReason = 'canvas_limit' | 'canvas_share' | 'detail_quota';

export interface PaywallIntent {
  trigger: PaywallTrigger;
  source: PaywallSource;
  reason?: PaywallReason;
  plan?: PlanSlug;
  /** Сразу открыть Paddle с выбранным планом (намерение с лендинга). */
  autoStart?: boolean;
}

interface PaywallContextValue {
  intent: PaywallIntent | null;
  /** false — предохранитель не пропустил (доступ не подтверждён или бесплатный режим). */
  openPaywall: (intent: PaywallIntent) => boolean;
  closePaywall: () => void;
}

const PaywallContext = createContext<PaywallContextValue | null>(null);

export function PaywallProvider({ children }: { children: ReactNode }) {
  const access = useAccess();
  const { user } = useAuth();
  const [intent, setIntent] = useState<PaywallIntent | null>(null);
  // Тарифы нужны баннеру, доку, окну и странице биллинга — греем кэш один раз (staleTime 10 мин).
  useSubscriptionPlans();
  // Paddle Retain: покупатель назван Paddle.js, у клиента Paddle SDK поднят заранее.
  usePaddleRetain();

  // BILL-8: в бесплатном режиме платёжная воронка не шлётся вовсе.
  useEffect(() => {
    setPaymentEventsEnabled(access.isPaywallEnabled);
  }, [access.isPaywallEnabled]);

  // Доступ появился (оплата, бесплатный режим) — окно больше не нужно.
  useEffect(() => {
    if (!canOpenPaywall(access)) setIntent(null);
  }, [access]);

  const openPaywall = useCallback(
    (next: PaywallIntent) => {
      if (!canOpenPaywall(access)) return false;
      if (next.trigger === 'tripwire')
        track('paywall_shown', {
          metadata: {
            source: next.source,
            // mode — режим стены (сейчас единственный hard), remaining — остаток
            // дневной квоты просмотров в момент показа: чистит знаменатель стены.
            mode: 'hard',
            remaining: user?.quota?.detail_views?.remaining ?? null,
          },
        });
      else track('subscribe_click', { metadata: { source: next.source, plan: next.plan ?? null } });
      setIntent(next);
      return true;
    },
    [access, user],
  );

  const closePaywall = useCallback(() => setIntent(null), []);

  const value = useMemo(
    () => ({ intent, openPaywall, closePaywall }),
    [intent, openPaywall, closePaywall],
  );
  return (
    <PaywallContext.Provider value={value}>
      {children}
      {intent ? (
        <PaywallModal
          key={`${intent.source}-${intent.plan ?? ''}`}
          intent={intent}
          onClose={closePaywall}
        />
      ) : null}
      {appConfig.apiMode === 'mock' ? <MockCheckout /> : null}
    </PaywallContext.Provider>
  );
}

export function usePaywall(): PaywallContextValue {
  const value = useContext(PaywallContext);
  if (!value) throw new Error('usePaywall must be used inside <PaywallProvider>');
  return value;
}
